import { useState } from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import { Building2, Stethoscope, Pill, Heart, Send, CheckCircle, Package, Baby } from 'lucide-react';

export default function Contracts() {
  const { t, isArabic } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    phone: '',
    email: '',
    message: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);

  const partners = [
    { icon: Building2, labelAr: 'المستشفيات', labelEn: 'Hospitals' },
    { icon: Stethoscope, labelAr: 'العيادات', labelEn: 'Clinics' },
    { icon: Pill, labelAr: 'الصيدليات', labelEn: 'Pharmacies' },
    { icon: Heart, labelAr: 'المراكز الطبية', labelEn: 'Medical Centers' },
    { icon: Package, labelAr: 'شركات الجملة', labelEn: 'Wholesale Companies' },
    { icon: Baby, labelAr: 'محلات الأم والطفل', labelEn: 'Mother & Baby Stores' },
  ];

  const benefits = [
    { ar: 'أسعار تنافسية خاصة للعقود', en: 'Competitive contract pricing' },
    { ar: 'توصيل مجاني داخل طرابلس', en: 'Free delivery in Tripoli' },
    { ar: 'دعم فني متكامل', en: 'Comprehensive technical support' },
    { ar: 'ضمان الجودة على جميع المنتجات', en: 'Quality guarantee on all products' },
    { ar: 'مرونة في الدفع', en: 'Flexible payment options' },
    { ar: 'خدمة عملاء متميزة', en: 'Excellent customer service' },
  ];

  const handleSubmit = (e) => {
    e.preventDefault();
    const mailtoLink = `mailto:albasma.alhadithaly@gmail.com?subject=${encodeURIComponent(
      isArabic ? 'طلب عقد توريد - ' + formData.company : 'Supply Contract Request - ' + formData.company
    )}&body=${encodeURIComponent(
      `${isArabic ? 'الاسم' : 'Name'}: ${formData.name}\n` +
      `${isArabic ? 'الشركة' : 'Company'}: ${formData.company}\n` +
      `${isArabic ? 'الهاتف' : 'Phone'}: ${formData.phone}\n` +
      `${isArabic ? 'البريد الإلكتروني' : 'Email'}: ${formData.email}\n\n` +
      `${isArabic ? 'الرسالة' : 'Message'}:\n${formData.message}`
    )}`;
    window.location.href = mailtoLink;
    setIsSubmitted(true);
    setTimeout(() => setIsSubmitted(false), 3000);
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <div data-testid="contracts-page">
      {/* Hero Section */}
      <section className="relative py-20 md:py-28 overflow-hidden" data-testid="contracts-hero">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-b from-slate-100 to-white" />
          <div 
            className="absolute inset-0 opacity-20"
            style={{
              backgroundImage: `linear-gradient(to right, #cbd5e1 1px, transparent 1px), linear-gradient(to bottom, #cbd5e1 1px, transparent 1px)`,
              backgroundSize: '60px 60px'
            }}
          />
        </div>

        <div className="container mx-auto px-4 md:px-6 max-w-7xl relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl"
          >
            <span className="inline-block px-4 py-2 bg-black text-white text-xs font-medium rounded-full mb-6 tracking-wider uppercase">
              {isArabic ? 'الشراكات' : 'Partnerships'}
            </span>
            <h1 className={`text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 mb-6 ${isArabic ? 'font-arabic' : 'font-english'}`}>
              {t('contractsTitle')}
            </h1>
            <p className="text-lg md:text-xl text-slate-600">
              {t('contractsSubtitle')}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Partners Section */}
      <section className="py-16 md:py-24 bg-white" data-testid="partners-section">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className={`text-3xl md:text-4xl font-bold text-slate-900 mb-4 ${isArabic ? 'font-arabic' : 'font-english'}`}>
              {t('contractsIntro')}
            </h2>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-16">
            {partners.map((partner, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-slate-50 rounded-2xl p-8 text-center hover:bg-slate-100 transition-colors duration-300"
                data-testid={`partner-card-${index}`}
              >
                <div className="w-16 h-16 rounded-full bg-black flex items-center justify-center mx-auto mb-4">
                  <partner.icon className="w-8 h-8 text-white" />
                </div>
                <h3 className={`text-lg font-semibold text-slate-900 ${isArabic ? 'font-arabic' : 'font-english'}`}>
                  {isArabic ? partner.labelAr : partner.labelEn}
                </h3>
              </motion.div>
            ))}
          </div>

          {/* Benefits */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="bg-black rounded-3xl p-8 md:p-12"
          >
            <h3 className={`text-2xl font-bold text-white mb-8 text-center ${isArabic ? 'font-arabic' : 'font-english'}`}>
              {isArabic ? 'مميزات العقود' : 'Contract Benefits'}
            </h3>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {benefits.map((benefit, index) => (
                <div key={index} className="flex items-center gap-3 text-white">
                  <CheckCircle className="w-5 h-5 text-slate-400 flex-shrink-0" />
                  <span className="text-sm">{isArabic ? benefit.ar : benefit.en}</span>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="py-16 md:py-24 bg-slate-50" data-testid="contract-form-section">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16">
            {/* Form Info */}
            <motion.div
              initial={{ opacity: 0, x: isArabic ? 30 : -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <span className="text-xs uppercase tracking-widest text-slate-500 mb-4 block">
                {isArabic ? 'تقدم الآن' : 'Apply Now'}
              </span>
              <h2 className={`text-3xl md:text-4xl font-bold text-slate-900 mb-6 ${isArabic ? 'font-arabic' : 'font-english'}`}>
                {t('requestContract')}
              </h2>
              <p className="text-slate-600 text-lg mb-8">
                {isArabic 
                  ? 'املأ النموذج التالي وسيتواصل معك فريقنا في أقرب وقت لمناقشة تفاصيل العقد'
                  : 'Fill out the form and our team will contact you soon to discuss contract details'
                }
              </p>
              
              <div className="bg-white rounded-2xl p-6 shadow-sm">
                <h4 className={`font-semibold text-slate-900 mb-4 ${isArabic ? 'font-arabic' : 'font-english'}`}>
                  {isArabic ? 'أو تواصل مباشرة' : 'Or contact us directly'}
                </h4>
                <a 
                  href="tel:0943999339"
                  className="flex items-center gap-3 text-black font-medium hover:text-slate-600 transition-colors"
                >
                  <span className="w-10 h-10 rounded-full bg-black text-white flex items-center justify-center text-sm">
                    <Send className="w-4 h-4" />
                  </span>
                  <span dir="ltr">0943 999 339</span>
                </a>
              </div>
            </motion.div>

            {/* Form */}
            <motion.div
              initial={{ opacity: 0, x: isArabic ? -30 : 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <form onSubmit={handleSubmit} className="bg-white rounded-2xl p-8 shadow-sm" data-testid="contract-form">
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      {t('name')} *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-black focus:ring-0 transition-colors"
                      data-testid="contract-name-input"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      {t('companyName')} *
                    </label>
                    <input
                      type="text"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-black focus:ring-0 transition-colors"
                      data-testid="contract-company-input"
                    />
                  </div>

                  <div className="grid sm:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        {t('phoneNumber')} *
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-black focus:ring-0 transition-colors"
                        dir="ltr"
                        data-testid="contract-phone-input"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        {t('emailAddress')}
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-black focus:ring-0 transition-colors"
                        dir="ltr"
                        data-testid="contract-email-input"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      {t('message')}
                    </label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      rows={4}
                      className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-black focus:ring-0 transition-colors resize-none"
                      data-testid="contract-message-input"
                    />
                  </div>

                  <button
                    type="submit"
                    className="w-full btn-primary flex items-center justify-center gap-2"
                    data-testid="contract-submit-btn"
                  >
                    {isSubmitted ? (
                      <>
                        <CheckCircle className="w-5 h-5" />
                        {isArabic ? 'تم الإرسال!' : 'Sent!'}
                      </>
                    ) : (
                      <>
                        <Send className="w-5 h-5" />
                        {t('sendRequest')}
                      </>
                    )}
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
}
